import React, { useState, useEffect } from 'react';
import { ExternalLink, ChevronDown, Link2, Users, MessageSquare, HelpCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const NA_TEXT = 'Not Available';

function isNA(val) {
  return !val || val.toLowerCase() === 'na';
}

function LinkRow({ label, url, badge }) {
  const unavailable = isNA(url);
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#1a1a1a] last:border-0 group">
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-300 font-medium">{label}</span>
        {badge && (
          <span className="text-[10px] font-rajdhani font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
            {badge}
          </span>
        )}
      </div>
      {unavailable ? (
        <span className="text-xs text-zinc-600 italic">{NA_TEXT}</span>
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-[#5865F2] hover:text-[#9aa7ff] transition-colors font-medium">
          Open <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

function WebsiteLinkRow({ label, url }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#1a1a1a] last:border-0">
      <span className="text-sm text-zinc-300 font-medium">{label}</span>
      {isNA(url) ? (
        <span className="text-xs text-zinc-600 italic">{NA_TEXT}</span>
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-rm-red hover:text-red-400 transition-colors font-medium">
          {url.replace(/^https?:\/\//, '')} <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

// ─── FAQ Data ─────────────────────────────────────────────────────────────
const FAQ = [
  {
    q: 'What is a Middleman (MM)?',
    a: 'A Middleman (MM) is a trusted third party who facilitates trades between buyers and sellers. The MM holds one side of the trade until both parties fulfil their end of the deal, reducing the risk of scams.'
  },
  {
    q: 'What does RobloxMart do?',
    a: 'RobloxMart is a free marketplace directory that connects Roblox item buyers and sellers. We do not process any payments — all trades happen outside our platform via Roblox or agreed methods. We simply list sellers and their items.'
  },
  {
    q: 'Is RobloxMart affiliated with Roblox Corporation?',
    a: 'No. RobloxMart is an independent community project and is NOT affiliated with, endorsed by, or associated with Roblox Corporation in any way.'
  },
  {
    q: 'How do I buy items?',
    a: 'Browse listings on RobloxMart, find an item you want, and click "Reveal Contact" to get the seller\'s Discord or contact info. Then reach out to the seller directly to arrange the trade. For safety, always use a middleman for high-value trades.'
  },
  {
    q: 'How do I become a seller?',
    a: 'Register for an account on RobloxMart, select "Seller" as your role during sign-up, verify your email, and you can immediately start creating listings. Add your contact methods (Discord, etc.) so buyers can reach you.'
  },
  {
    q: 'Is the free MM service available 24/7?',
    a: 'Yes! The RobloxMart free autoMM service is available 24/7. It is fully automated, so you can use it any time — no need to wait for anyone to be online.'
  },
  {
    q: 'What happens if I get scammed?',
    a: 'Report the seller using the "Report" button on their profile page. Our admin team reviews all reports. To avoid scams, always use a middleman and never trade outside the agreed method.'
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#1e1e1e] rounded-sm overflow-hidden transition-all duration-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.02] transition-colors group"
        data-testid={`faq-${q.replace(/\s+/g, '-').toLowerCase().slice(0, 30)}`}
      >
        <span className="text-sm font-semibold text-zinc-200 group-hover:text-white transition-colors pr-4">{q}</span>
        <ChevronDown
          size={16}
          className={`text-rm-red shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-96' : 'max-h-0'}`}>
        <div className="px-5 pb-4 text-sm text-zinc-400 leading-relaxed border-t border-[#1a1a1a] pt-4">
          {a}
        </div>
      </div>
    </div>
  );
}

export default function LinksAndFAQ() {
  const [linksConfig, setLinksConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/links-config`)
      .then(r => r.json())
      .then(data => setLinksConfig(data))
      .catch(() => setLinksConfig({ website_links: [], middleman_services: [], discord_servers: [] }))
      .finally(() => setLoading(false));
  }, []);

  const websiteLinks = linksConfig?.website_links || [];
  const mmServices = linksConfig?.middleman_services || [];
  const discordServers = linksConfig?.discord_servers || [];

  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12">

        {/* Header */}
        <div className="mb-10">
          <p className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.3em] text-rm-red mb-1">Resources</p>
          <h1 className="font-rajdhani font-bold text-4xl md:text-5xl uppercase tracking-tight" data-testid="links-title">
            Links &amp; <span className="text-zinc-600">FAQ</span>
          </h1>
          <p className="text-rm-muted text-sm mt-2">All official RobloxMart links and frequently asked questions in one place.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-7 h-7 border-2 border-rm-red border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ── Website Links ───────────────────────────────────────── */}
            {websiteLinks.length > 0 && (
              <section className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Link2 size={14} className="text-rm-red" />
                  <h2 className="font-rajdhani font-bold text-xs uppercase tracking-[0.2em] text-zinc-500">Website Links</h2>
                </div>
                <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm px-5 divide-y divide-[#1a1a1a]">
                  {websiteLinks.map(item => (
                    <WebsiteLinkRow key={item.id} label={item.label} url={item.url} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Middleman Services ──────────────────────────────────── */}
            {mmServices.length > 0 && (
              <section className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Users size={14} className="text-rm-red" />
                  <h2 className="font-rajdhani font-bold text-xs uppercase tracking-[0.2em] text-zinc-500">Middleman Services</h2>
                </div>
                <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm px-5">
                  {mmServices.map(item => (
                    <LinkRow key={item.id} label={item.label} url={item.url} badge={item.badge} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Discord Servers ─────────────────────────────────────── */}
            {discordServers.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare size={14} className="text-rm-red" />
                  <h2 className="font-rajdhani font-bold text-xs uppercase tracking-[0.2em] text-zinc-500">Discord Roblox Marketplace</h2>
                </div>
                <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm px-5">
                  {discordServers.map(item => (
                    <LinkRow key={item.id} label={item.label} url={item.url} badge={item.badge} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* ── FAQ ─────────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle size={14} className="text-rm-red" />
            <h2 className="font-rajdhani font-bold text-xs uppercase tracking-[0.2em] text-zinc-500">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-2">
            {FAQ.map((item) => (
              <FAQItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </section>

      </div>
    </div>
  );
}
